import React from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

import { formatMoney } from '../../../utils/formatMoney';
import type { DashboardKpi } from '../types';

type DashboardKpiCardProps = {
  label: string;
  kpi: DashboardKpi;
  comparisonLabel?: string;
  /** Zweite Zeile unter dem Wert, z. B. das Volumen zur Anzahl. */
  detail?: string;
};

function formatValue(kpi: DashboardKpi): string {
  if (kpi.format === 'money') return formatMoney(kpi.value);
  return kpi.value.toLocaleString('de-DE');
}

function formatDelta(kpi: DashboardKpi): string {
  const sign = kpi.deltaAbs > 0 ? '+' : kpi.deltaAbs < 0 ? '−' : '±';
  if (kpi.deltaKind === 'abs') {
    return `${sign}${Math.abs(kpi.deltaAbs).toLocaleString('de-DE')}`;
  }
  if (kpi.deltaPct != null) {
    return `${sign}${Math.abs(kpi.deltaPct).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
  }
  if (kpi.previousValue === 0 && kpi.value > 0) return 'neu';
  if (kpi.deltaAbs === 0) return '±0 %';
  return '';
}

function hasTrend(kpi: DashboardKpi): boolean {
  if (kpi.deltaKind === 'abs') return kpi.deltaAbs !== 0;
  if (kpi.deltaPct != null) return true;
  if (kpi.previousValue === 0 && kpi.value > 0) return true;
  return kpi.deltaAbs !== 0;
}

export function DashboardKpiCard({ label, kpi, comparisonLabel = 'vs. vorh. Zeitraum', detail }: DashboardKpiCardProps) {
  const positive = kpi.deltaAbs > 0;
  const negative = kpi.deltaAbs < 0;
  const deltaText = formatDelta(kpi);
  const showTrend = hasTrend(kpi);
  const showComparison = Boolean(comparisonLabel);

  const trendClass = positive
    ? 'text-emerald-600'
    : negative
      ? 'text-rose-500'
      : 'text-slate-400';

  return (
    <div className="rounded-xl border border-app-border bg-app-surface p-4 shadow-sm">
      <p className="text-xs font-semibold text-app-text-subtle">{label}</p>
      <p className="mt-2 text-2xl font-bold text-app-text">{formatValue(kpi)}</p>
      {detail ? <p className="mt-0.5 text-xs font-medium text-app-text-muted tabular-nums">{detail}</p> : null}
      {showComparison ? (
        <p className={`mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs font-medium ${showTrend ? trendClass : 'text-slate-400'}`}>
          {showTrend && positive ? (
            <ArrowUpRight className="h-3.5 w-3.5" />
          ) : showTrend && negative ? (
            <ArrowDownRight className="h-3.5 w-3.5" />
          ) : null}
          {deltaText ? <span>{deltaText}</span> : null}
          <span className="text-slate-400 font-normal">{comparisonLabel}</span>
        </p>
      ) : (
        <p className="mt-1 text-xs text-slate-400">Aktueller Bestand</p>
      )}
    </div>
  );
}
